import { Box, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { useParams } from "react-router-dom";
import { getLive, liveArchive } from "@/content/live";
import { StreamPlayer } from "@/components/video/StreamPlayer";
import { LiveTile } from "@/components/live/LiveTile";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHero } from "@/components/layout/PageHero";
import { PageContainer } from "@/components/layout/PageContainer";
import { formatDuration } from "@/lib/format";

export default function LivePage() {
  const { slug } = useParams<{ slug: string }>();
  const show = slug ? getLive(slug) : undefined;

  if (!show) {
    return (
      <>
        <PageHero title="Not found" />
        <PageContainer navOffset={false}>
          <EmptyState title="Show not found" body={`No live entry at "${slug}".`} />
        </PageContainer>
      </>
    );
  }

  const otherVr = liveArchive.filter((l) => l.format === "vr" && l.slug !== show.slug);

  return (
    <>
      <PageHero
        title={show.title}
        subtitle={`${show.venue}${show.format === "vr" ? " · VR" : ""}`}
      />
      <PageContainer navOffset={false}>
        <Stack gap="xl">
          {show.video && <StreamPlayer video={show.video} />}

          {show.description && (
            <Text size="md" c="var(--color-foam)" maw={760}>
              {show.description}
            </Text>
          )}

          {show.tracklist && show.tracklist.length > 0 && (
            <Box>
              <Title order={2} size="h3" mb="sm">
                Tracklist
              </Title>
              <Stack gap="xs">
                {show.tracklist.map((t, i) => (
                  <Box
                    key={i}
                    p="sm"
                    style={{
                      background: "var(--color-deep)",
                      borderRadius: 6,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text>
                      {t.title}
                      {t.artist && (
                        <Text component="span" c="dimmed">
                          {" "}
                          — {t.artist}
                        </Text>
                      )}
                    </Text>
                    {typeof t.atSec === "number" && (
                      <Text size="xs" c="dimmed" ff="monospace">
                        {formatDuration(t.atSec)}
                      </Text>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {otherVr.length > 0 && (
            <Box>
              <Title order={2} size="h3" mb="sm">
                More VR shows
              </Title>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                {otherVr.map((s) => (
                  <LiveTile key={s.slug} show={s} />
                ))}
              </SimpleGrid>
            </Box>
          )}
        </Stack>
      </PageContainer>
    </>
  );
}
